import React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useTheme } from "@repo/tokens";
import {
  DoubtsAiService,
  getDc1GetGeneratedSolution,
  getGetDc1GetGeneratedSolutionQueryKey,
  usePostDc1GenerateQuestionSolution,
  usePutDc1V2AutosolveDoubtId,
  useGetFbFeedbackPrompts,
  usePostFbFeedbackStorefeedback,
} from "@oneapp/data";
import {
  Icon,
  Modal,
  resolveUserAccountPhotoToIconName,
  userAccountPhotoShouldUseInitials,
} from "@repo/ui";
import { getInitialsFromName } from "@repo/utils";
import { useDoubtData } from "../../../lib/DoubtContext";
import { refreshDoubtsAiAfterSessionClose } from "../../../lib/refreshDoubtsAiAfterSession";
import { extractRagSolution } from "../../../lib/ragSolution";
import { useDoubtErrorToast } from "../../../lib/doubtErrorToast";
import { isDoubtBuddySocketErrorMarkdown } from "../../../lib/doubtBuddySocketError";
import { extractToRagSolution } from "./doubtBuddyExtractSections";
import type { MockRagSolution } from "./mockRagData";
import { DEFAULT_MOCK_RAG_SOLUTION } from "./mockRagData";
import type { GetAnswersChatSession } from "./types";
import { useDoubtBuddyRevise } from "../../../hooks/useDoubtBuddyRevise";
import { MarkdownSolution } from "./MarkdownSolution";
import { MarkdownInline } from "./MarkdownInline";
import { SolutionLoadingBubble } from "./SolutionLoadingBubble";
import "katex/dist/katex.min.css";
import "./getAnswersChat.css";

const MOCK_LOADING_MS = 1800;
const RESOLVED_AUTO_CLOSE_MS = 3000;
const FEEDBACK_TIMER_SECONDS = 30;

type FeedbackPrompt = {
  id: number;
  tag: string;
  prompt: string;
  tenantId: number;
};

function getNotResolvedPillLabel(prompt: FeedbackPrompt): string {
  const normalized = `${prompt.tag} ${prompt.prompt}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (
    normalized.includes("unrelated") &&
    (normalized.includes("suggestion") || normalized.includes("step"))
  ) {
    return "Unrelated Suggestions/Steps";
  }
  if (normalized.includes("incorrect") && normalized.includes("answer")) {
    return "Incorrect Answer";
  }
  if (normalized.includes("solution") && normalized.includes("unclear")) {
    return "Solution Unclear";
  }
  if (
    normalized.includes("just want") ||
    (normalized.includes("want") && normalized.includes("answer"))
  ) {
    return "I Just Want Answer";
  }

  return prompt.prompt;
}

type RagSolution = {
  answer: string;
  hints: string[];
  steps: string[];
};

type DisplayStep = {
  title: string;
  content: string[];
};

type CloseFlowStep = "none" | "leave" | "resolved";
type FeedbackState = "pending" | "resolving" | "resolved" | "not-resolved";

function extractStepHeading(step: string): string | null {
  const normalized = step
    .replace(/\\textbf\{/g, "")
    .replace(/\}/g, "")
    .replace(/\$/g, "")
    .trim();
  const match = normalized.match(/^Step\s*\d+\s*:[\s\S]*$/i);
  return match ? normalized : null;
}

function buildDisplaySteps(steps: string[]): {
  preface: string[];
  displaySteps: DisplayStep[];
} {
  const hasExplicitStepHeadings = steps.some((step) =>
    extractStepHeading(step),
  );
  if (!hasExplicitStepHeadings) {
    return {
      preface: [],
      displaySteps: steps.map((step, index) => ({
        title: `Step ${index + 1}:`,
        content: [step],
      })),
    };
  }

  const preface: string[] = [];
  const displaySteps: DisplayStep[] = [];

  for (const step of steps) {
    const heading = extractStepHeading(step);
    if (heading) {
      displaySteps.push({ title: heading, content: [] });
    } else if (displaySteps.length > 0) {
      displaySteps[displaySteps.length - 1]?.content.push(step);
    } else {
      preface.push(step);
    }
  }

  return { preface, displaySteps };
}

function AssistantRagPanels({
  rag,
  isLoading,
  keyBase,
  theme,
  t,
  centerLoadingInPane = false,
}: {
  rag: RagSolution;
  isLoading: boolean;
  keyBase: string;
  theme: ReturnType<typeof useTheme>["theme"];
  t: (key: string) => string;
  centerLoadingInPane?: boolean;
}) {
  if (isLoading) {
    const wrapperClassName = [
      "gac-solution-loading-outer",
      centerLoadingInPane ? "gac-solution-loading-outer--pane-center" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <SolutionLoadingBubble
        loadingAriaLabel={t("chat_loading_a11y")}
        inAinaStack={centerLoadingInPane}
        wrapperClassName={wrapperClassName}
      />
    );
  }

  const hasHints = rag.hints.length > 0;
  const hasSteps = rag.steps.length > 0;
  const hasAnswer = rag.answer.trim().length > 0;
  const { preface: stepPreface, displaySteps } = buildDisplaySteps(rag.steps);

  return (
    <>
      {hasHints ? (
        <div className="gac-hint-box" key={`${keyBase}-hint-0`}>
          <div className="gac-hint-row">
            <Icon
              name="hintsDoubtIcon"
              size={24}
              color={theme.colors.neutral[10]}
            />
            <p className="gac-hint-label">{t("hint")}</p>
          </div>
          <div className="gac-hint-body">
            <MarkdownInline>{rag.hints[0] ?? ""}</MarkdownInline>
          </div>
        </div>
      ) : null}

      {hasSteps ? (
        <div className="gac-steps-box">
          <div className="gac-hint-row">
            <Icon
              name="stepsDoubtIcon"
              size={24}
              color={theme.colors.neutral[10]}
            />
            <p className="gac-steps-label">{t("solution_steps")}</p>
          </div>
          {stepPreface.map((step: string, index: number) => (
            <div
              className="gac-step-desc"
              key={`${keyBase}-step-preface-${index}`}
            >
              <MarkdownInline>{step}</MarkdownInline>
            </div>
          ))}
          {displaySteps.map((step, index: number) => (
            <div key={`${keyBase}-step-${index}`}>
              <p className="gac-step-title">{step.title}</p>
              <div className="gac-step-desc">
                {step.content.map((line, lineIndex) => (
                  <MarkdownInline key={`${keyBase}-step-${index}-${lineIndex}`}>
                    {line}
                  </MarkdownInline>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {hasAnswer ? (
        <div className="gac-solution-box">
          <div className="gac-hint-row">
            <Icon
              name="solutionDoubtIcon"
              size={24}
              color={theme.colors.neutral[10]}
            />
            <p className="gac-solution-label">{t("solution")}</p>
          </div>
          <div className="gac-solution-body">
            <MarkdownInline>{rag.answer}</MarkdownInline>
          </div>
        </div>
      ) : null}
    </>
  );
}

function ragHasContent(r: RagSolution): boolean {
  return r.answer.trim().length > 0 || r.hints.length > 0 || r.steps.length > 0;
}

function UserChatAvatar() {
  const { firstName, lastName, profilePhoto } = useDoubtData();
  if (userAccountPhotoShouldUseInitials(profilePhoto)) {
    const displayName = [(firstName ?? "").trim(), (lastName ?? "").trim()]
      .filter(Boolean)
      .join(" ");
    const initials = getInitialsFromName(displayName) || "U";
    return (
      <span className="gac-avatar-initials" aria-hidden>
        {initials}
      </span>
    );
  }
  return (
    <Icon
      name={resolveUserAccountPhotoToIconName(profilePhoto)}
      width={31}
      height={31}
    />
  );
}

/** Angular `getDoubtsBuddyText` / GET .../doubtbuddy/:messageId/getAnswer */
function readStoredBuddyAnswerFromGetAnswer(res: unknown): string | undefined {
  if (res == null || typeof res !== "object") return undefined;
  const r = res as Record<string, unknown>;
  const data =
    r.data != null && typeof r.data === "object"
      ? (r.data as Record<string, unknown>)
      : undefined;
  const da = r.doubtAnswer as Record<string, unknown> | undefined;
  const daNested = data?.doubtAnswer as Record<string, unknown> | undefined;
  const raw = da?.answer ?? daNested?.answer ?? data?.answer ?? r.answer;
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (!trimmed.length || isDoubtBuddySocketErrorMarkdown(trimmed)) {
    return undefined;
  }
  return trimmed;
}

function isValidBuddySocketMarkdown(
  md: string | null | undefined,
): md is string {
  const trimmed = typeof md === "string" ? md.trim() : "";
  return trimmed.length > 0 && !isDoubtBuddySocketErrorMarkdown(trimmed);
}

function readMessageIdFromSetBuddyResponse(
  res: unknown,
): string | number | undefined {
  if (res == null || typeof res !== "object") return undefined;
  const r = res as Record<string, unknown>;
  const nested =
    r.data != null && typeof r.data === "object"
      ? (r.data as Record<string, unknown>)
      : undefined;
  const raw = nested?.messageId ?? r.messageId;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  if (typeof raw === "string" && raw.length > 0) {
    const s = raw.trim();
    const n = Number(s);
    if (Number.isFinite(n) && n > 0 && String(n) === s) return n;
    return s;
  }
  return undefined;
}

export type GetAnswersChatCloseOptions = {
  navigateToAina?: boolean;
  navigateToHref?: string;
};

export type GetAnswersChatHandle = {
  requestBreadcrumbNavigation: (href: string) => void;
};

export type GetAnswersChatProps = {
  session: GetAnswersChatSession;
  userFirstName?: string;
  onClose: (options?: GetAnswersChatCloseOptions) => void;
  onDoubtResolved?: (doubtId: number) => void;
  mockSolution?: MockRagSolution;
  layout?: "embedded" | "fullscreen";
  ainaEmbedded?: boolean;
};

export const GetAnswersChat = React.forwardRef<
  GetAnswersChatHandle,
  GetAnswersChatProps
>(function GetAnswersChat(
  {
    session,
    userFirstName = "",
    onClose,
    onDoubtResolved,
    mockSolution,
    layout = "embedded",
    ainaEmbedded = false,
  },
  ref,
) {
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();
  const doubtSession = useDoubtData();
  const queryClient = useQueryClient();
  const { showSomethingWentWrong } = useDoubtErrorToast();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [chatScrollElevated, setChatScrollElevated] = React.useState(false);
  const pendingBreadcrumbHrefRef = React.useRef<string | null>(null);
  const isMock = mockSolution != null;
  const needsRefreshOnCloseRef = React.useRef(false);

  const [mockLoading, setMockLoading] = React.useState(true);
  const [disclaimerOpen, setDisclaimerOpen] = React.useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = React.useState(false);
  const [closeFlowStep, setCloseFlowStep] =
    React.useState<CloseFlowStep>("none");
  const [isRefreshingDoubtsAi, setIsRefreshingDoubtsAi] = React.useState(false);
  const [feedbackState, setFeedbackState] =
    React.useState<FeedbackState>("pending");
  const [feedbackTimerSecondsLeft, setFeedbackTimerSecondsLeft] =
    React.useState(FEEDBACK_TIMER_SECONDS);
  const [selectedPromptIds, setSelectedPromptIds] = React.useState<Set<number>>(
    () => new Set(),
  );
  const [feedbackCommitted, setFeedbackCommitted] = React.useState(false);
  const [awaitingPostReviseResolution, setAwaitingPostReviseResolution] =
    React.useState(false);
  const [reviseSolutionRevealed, setReviseSolutionRevealed] =
    React.useState(false);
  const [reviseFeedbackChoice, setReviseFeedbackChoice] = React.useState<
    "resolved" | "not-resolved" | null
  >(null);
  const [finalFeedbackChoice, setFinalFeedbackChoice] = React.useState<
    "resolved" | "not-resolved" | null
  >(null);
  const [buddyPostReviseFeedbackPrompt, setBuddyPostReviseFeedbackPrompt] =
    React.useState(false);
  const [initialGenerateBuddyFallback, setInitialGenerateBuddyFallback] =
    React.useState(false);

  const showAinaReturnCta = session.fromAinaHost === true;

  const showAinaSolutionAvatar = session.fromAinaHost === true;
  const [ainaAllowBackToMain, setAinaAllowBackToMain] = React.useState(false);

  const initialOpeningDoubtIdRef = React.useRef<number | null>(null);
  const [initialAssistant, setInitialAssistant] =
    React.useState<RagSolution | null>(null);
  const sessionIdentityKeyRef = React.useRef<string>("");
  const solutionLoadGenRef = React.useRef(0);
  const [cachedSolutionFetchPending, setCachedSolutionFetchPending] =
    React.useState(false);

  const sessionIdentityKey =
    session.kind === "image"
      ? `image:${session.doubtId}:${session.subjectId}:${session.imageUrl}:${session.questionText}`
      : `text:${session.doubtId}:${session.subjectId}:${session.questionText}`;

  const {
    mutateAsync: generateSolutionAsync,
    isPending: isGeneratePending,
    isError: isGenerateError,
    reset: resetGenerate,
  } = usePostDc1GenerateQuestionSolution({
    request: { clientName: "doubtsDCAPI" },
  });

  const { mutateAsync: putAutosolveAsync, isPending: isAutosolvePending } =
    usePutDc1V2AutosolveDoubtId({
      request: { clientName: "doubtsDCAPI" },
    });

  const { data: feedbackPromptsData } = useGetFbFeedbackPrompts({
    query: {
      enabled: feedbackState === "not-resolved",
      staleTime: Infinity,
    },
    request: { clientName: "doubtsDCAPI" },
  });
  const feedbackPrompts: FeedbackPrompt[] =
    (feedbackPromptsData as { feedBackPrompts?: FeedbackPrompt[] })
      ?.feedBackPrompts ?? [];

  const { mutateAsync: storeFeedbackAsync, isPending: isStoreFeedbackPending } =
    usePostFbFeedbackStorefeedback({
      request: { clientName: "doubtsDCAPI" },
    });

  const {
    phase: revisePhase,
    finalMarkdown: reviseFinalMarkdown,
    error: reviseError,
    startRevise,
    disconnect: disconnectRevise,
  } = useDoubtBuddyRevise();

  const { mutateAsync: setDoubtBuddyAnswerAsync } =
    DoubtsAiService.usePostAiDoubtbuddySetAnswerByDoubtBuddy({
      request: { clientName: "doubtsAi" },
    });

  /** Dedupe `POST .../setAnswerByDoubtBuddy` when the same markdown is already stored. */
  const buddyPersistedAnswerRef = React.useRef<string | null>(null);
  /** Updated after a successful save (Angular also updates `doubtBuddyMessageId` from response). */
  const buddyMessageIdRef = React.useRef<string | number | undefined>(
    session.doubtBuddyMessageId,
  );

  const closingSessionBusy = isAutosolvePending || isRefreshingDoubtsAi;

  React.useEffect(() => {
    if (sessionIdentityKeyRef.current === sessionIdentityKey) {
      return;
    }
    sessionIdentityKeyRef.current = sessionIdentityKey;
    initialOpeningDoubtIdRef.current = session.doubtId;
    setInitialAssistant(null);
    setFeedbackState("pending");
    setFeedbackTimerSecondsLeft(FEEDBACK_TIMER_SECONDS);
    setSelectedPromptIds(new Set());
    setFeedbackCommitted(false);
    setAwaitingPostReviseResolution(false);
    setReviseSolutionRevealed(false);
    setReviseFeedbackChoice(null);
    setFinalFeedbackChoice(null);
    setBuddyPostReviseFeedbackPrompt(false);
    setInitialGenerateBuddyFallback(false);
    setChatScrollElevated(false);
    resetGenerate();
    setAinaAllowBackToMain(false);
    buddyPersistedAnswerRef.current = null;
    buddyMessageIdRef.current = session.doubtBuddyMessageId;
    disconnectRevise();
  }, [
    disconnectRevise,
    resetGenerate,
    session.doubtBuddyMessageId,
    session.doubtId,
    sessionIdentityKey,
  ]);

  React.useEffect(() => {
    if (revisePhase === "connecting") {
      setAwaitingPostReviseResolution(false);
      setReviseSolutionRevealed(false);
    }
  }, [revisePhase]);

  React.useEffect(() => {
    if (revisePhase !== "complete") return;
    setAwaitingPostReviseResolution(true);
    setFeedbackState("pending");
    setFeedbackTimerSecondsLeft(
      reviseFeedbackChoice ? 0 : FEEDBACK_TIMER_SECONDS,
    );
  }, [reviseFeedbackChoice, revisePhase]);

  const lastSocketErrorToastRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (revisePhase !== "error" || !reviseError) return;
    if (lastSocketErrorToastRef.current === reviseError) return;
    lastSocketErrorToastRef.current = reviseError;
    showSomethingWentWrong();
  }, [reviseError, revisePhase, showSomethingWentWrong]);

  React.useEffect(() => {
    if (revisePhase !== "error") {
      lastSocketErrorToastRef.current = null;
    }
  }, [revisePhase]);

  React.useEffect(() => {
    if (!isMock) {
      setMockLoading(false);
      return;
    }
    setMockLoading(true);
    const tId = window.setTimeout(() => setMockLoading(false), MOCK_LOADING_MS);
    return () => window.clearTimeout(tId);
  }, [isMock, session]);

  React.useEffect(() => {
    if (!isMock) return;
    if (mockLoading) {
      setInitialAssistant(null);
    } else {
      setInitialAssistant(mockSolution ?? DEFAULT_MOCK_RAG_SOLUTION);
    }
  }, [isMock, mockLoading, mockSolution]);

  const runFetchCachedSolutionThenGenerate = React.useCallback(
    (ctx: {
      doubtId: number;
      grade: number;
      questionText: string;
      subjectForSolution: string;
    }) => {
      const gen = (solutionLoadGenRef.current += 1);
      void (async () => {
        setCachedSolutionFetchPending(true);
        const { doubtId: id, grade, questionText, subjectForSolution } = ctx;

        const buddyMsgId = session.doubtBuddyMessageId;
        if (!isMock && buddyMsgId != null) {
          const sid =
            typeof buddyMsgId === "string"
              ? buddyMsgId.trim()
              : String(buddyMsgId);
          if (sid.length > 0) {
            try {
              const buddyRes =
                await DoubtsAiService.getAiDoubtbuddyMessageIDGetAnswer(sid, {
                  clientName: "doubtsAi",
                });
              if (gen !== solutionLoadGenRef.current) {
                setCachedSolutionFetchPending(false);
                return;
              }
              const raw = readStoredBuddyAnswerFromGetAnswer(buddyRes);
              if (raw) {
                const fromBuddy = extractToRagSolution(raw);
                if (ragHasContent(fromBuddy)) {
                  setInitialAssistant(fromBuddy);
                  setCachedSolutionFetchPending(false);
                  return;
                }
              }
            } catch {
              /* fall through to cache / generate */
            }
          }
        }

        try {
          const cached = await queryClient.fetchQuery({
            queryKey: getGetDc1GetGeneratedSolutionQueryKey({ doubtId: id }),
            queryFn: ({ signal }) =>
              getDc1GetGeneratedSolution(
                { doubtId: id },
                { clientName: "doubtsDCAPI" },
                signal,
              ),
          });
          if (gen !== solutionLoadGenRef.current) {
            setCachedSolutionFetchPending(false);
            return;
          }
          const parsed = extractRagSolution(cached as unknown);
          if (ragHasContent(parsed)) {
            setInitialAssistant(parsed);
            setCachedSolutionFetchPending(false);
            return;
          }
        } catch {
          /* fall through to generate */
        }
        if (gen !== solutionLoadGenRef.current) {
          setCachedSolutionFetchPending(false);
          return;
        }
        setCachedSolutionFetchPending(false);
        try {
          const result = await generateSolutionAsync({
            data: {
              doubtId: id,
              data: {
                question: questionText,
                grade,
                subject: subjectForSolution,
              },
            },
          });
          if (gen !== solutionLoadGenRef.current) {
            return;
          }
          const generated = extractRagSolution(result as unknown);
          if (ragHasContent(generated)) {
            setInitialAssistant(generated);
            queryClient.setQueryData(
              getGetDc1GetGeneratedSolutionQueryKey({ doubtId: id }),
              result,
            );
            return;
          }
          setInitialGenerateBuddyFallback(true);
          resetGenerate();
          startRevise(
            session.kind === "image"
              ? {
                  questionText,
                  imageUrl: session.imageUrl,
                }
              : { questionText },
          );
        } catch {
          if (gen !== solutionLoadGenRef.current) {
            return;
          }
          setInitialGenerateBuddyFallback(true);
          resetGenerate();
          startRevise(
            session.kind === "image"
              ? {
                  questionText,
                  imageUrl: session.imageUrl,
                }
              : { questionText },
          );
        }
      })();
    },
    [
      generateSolutionAsync,
      isMock,
      queryClient,
      resetGenerate,
      session,
      startRevise,
    ],
  );

  React.useEffect(() => {
    if (isMock) return;
    const id = session.doubtId;
    if (!id || id <= 0) return;
    const grade = doubtSession.gradeId;
    if (grade == null) return;

    runFetchCachedSolutionThenGenerate({
      doubtId: id,
      grade,
      questionText: session.questionText,
      subjectForSolution: session.subjectForSolution,
    });

    return () => {
      solutionLoadGenRef.current += 1;
      setCachedSolutionFetchPending(false);
    };
  }, [
    doubtSession.gradeId,
    isMock,
    runFetchCachedSolutionThenGenerate,
    session.doubtId,
    session.questionText,
    session.subjectForSolution,
    session.doubtBuddyMessageId,
    sessionIdentityKey,
  ]);

  const emptyRag: RagSolution = { answer: "", hints: [], steps: [] };

  const reviseInProgressInitialBuddy =
    initialGenerateBuddyFallback &&
    (revisePhase === "connecting" ||
      revisePhase === "processing" ||
      revisePhase === "streaming");

  const initialAssistantLoading = isMock
    ? mockLoading
    : !initialAssistant &&
      !isGenerateError &&
      !initialGenerateBuddyFallback &&
      (cachedSolutionFetchPending || isGeneratePending);

  const showBuddyFallbackError =
    initialGenerateBuddyFallback && revisePhase === "error";

  const showGenerateError =
    !isMock &&
    !isGeneratePending &&
    !initialAssistant &&
    (showBuddyFallbackError ||
      (isGenerateError && !initialGenerateBuddyFallback));

  const solutionReady =
    (initialAssistant != null && ragHasContent(initialAssistant)) ||
    (initialGenerateBuddyFallback &&
      revisePhase === "complete" &&
      isValidBuddySocketMarkdown(reviseFinalMarkdown));

  const showAssistantRagPanels =
    (initialAssistant != null || initialAssistantLoading) &&
    !reviseInProgressInitialBuddy;

  const normalizedDoubtStatus = (session.doubtStatus ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  const statusSuppressesResolutionFeedback =
    normalizedDoubtStatus === "RESOLVED" ||
    normalizedDoubtStatus === "DISMISSED" ||
    normalizedDoubtStatus === "NOT_RESOLVED" ||
    normalizedDoubtStatus === "NOTRESOLVED";
  const showResolutionFeedbackUi =
    session.showResolutionFeedback !== false &&
    !statusSuppressesResolutionFeedback;

  const syncChatScrollElevation = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setChatScrollElevated(false);
      return;
    }
    const canScroll = el.scrollHeight > el.clientHeight + 1;
    setChatScrollElevated(canScroll && el.scrollTop > 1);
  }, []);

  const scrollToBottom = React.useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
    syncChatScrollElevation();
  }, [syncChatScrollElevation]);

  const handleChatScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const canScroll = el.scrollHeight > el.clientHeight + 1;
      setChatScrollElevated(canScroll && el.scrollTop > 1);
    },
    [],
  );

  React.useEffect(() => {
    scrollToBottom();
  }, [
    initialAssistantLoading,
    initialAssistant,
    reviseSolutionRevealed,
    feedbackState,
    scrollToBottom,
  ]);

  React.useEffect(() => {
    if (
      !showResolutionFeedbackUi ||
      !solutionReady ||
      feedbackState !== "pending" ||
      reviseFeedbackChoice
    )
      return;
    if (feedbackTimerSecondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setFeedbackTimerSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [
    showResolutionFeedbackUi,
    solutionReady,
    feedbackState,
    feedbackTimerSecondsLeft,
    reviseFeedbackChoice,
  ]);

  const handleRetryGenerate = React.useCallback(() => {
    resetGenerate();
    setInitialAssistant(null);
    setInitialGenerateBuddyFallback(false);
    disconnectRevise();
    const id = session.doubtId;
    const grade = doubtSession.gradeId;
    if (!id || id <= 0 || grade == null) return;
    runFetchCachedSolutionThenGenerate({
      doubtId: id,
      grade,
      questionText: session.questionText,
      subjectForSolution: session.subjectForSolution,
    });
  }, [
    disconnectRevise,
    doubtSession.gradeId,
    resetGenerate,
    runFetchCachedSolutionThenGenerate,
    session.doubtBuddyMessageId,
    session.doubtId,
    session.questionText,
    session.subjectForSolution,
  ]);

  const resolvedFirstName =
    userFirstName || doubtSession.firstName || t("chat_default_user_name");

  const intro = t("chat_answer_intro", {
    name: resolvedFirstName,
  });
  const feedbackThanksMessage = (
    <p className="gac-assistant-intro-text">
      {t("thanks_feedback", {
        name: resolvedFirstName,
      })}
    </p>
  );

  const handleLeaveModalCancel = React.useCallback(() => {
    setCloseFlowStep("none");
    pendingBreadcrumbHrefRef.current = null;
  }, []);

  const emitClose = React.useCallback(
    (extra?: GetAnswersChatCloseOptions) => {
      setCloseFlowStep("none");
      if (extra?.navigateToAina) {
        pendingBreadcrumbHrefRef.current = null;
        onClose({ navigateToAina: true });
        return;
      }
      const pending = pendingBreadcrumbHrefRef.current;
      pendingBreadcrumbHrefRef.current = null;
      if (pending != null && pending !== "") {
        onClose({ ...extra, navigateToHref: pending });
        return;
      }
      onClose(extra);
    },
    [onClose],
  );

  const refreshDoubtsAiIfNeeded = React.useCallback(async () => {
    if (isMock) return;
    if (!needsRefreshOnCloseRef.current) return;
    needsRefreshOnCloseRef.current = false;

    setIsRefreshingDoubtsAi(true);
    try {
      if (doubtSession.uamId != null && doubtSession.tenantId) {
        try {
          await refreshDoubtsAiAfterSessionClose(queryClient, {
            userId: doubtSession.uamId,
            tenantId: doubtSession.tenantId,
            dcNumericTenantId: doubtSession.dcNumericTenantId,
            gradeId: doubtSession.gradeId,
          });
        } catch {
          /* best-effort */
        }
      }
    } finally {
      setIsRefreshingDoubtsAi(false);
    }
  }, [
    doubtSession.dcNumericTenantId,
    doubtSession.gradeId,
    doubtSession.tenantId,
    doubtSession.uamId,
    isMock,
    queryClient,
  ]);

  const finishCloseChat = React.useCallback(
    (extra?: GetAnswersChatCloseOptions) => {
      void (async () => {
        await refreshDoubtsAiIfNeeded();
        emitClose(extra);
      })();
    },
    [emitClose, refreshDoubtsAiIfNeeded],
  );

  const handleLeaveModalConfirm = React.useCallback(() => {
    if (!showResolutionFeedbackUi && !showAinaReturnCta) {
      finishCloseChat();
      return;
    }
    if (feedbackCommitted) {
      finishCloseChat();
      return;
    }
    setCloseFlowStep("resolved");
  }, [
    feedbackCommitted,
    finishCloseChat,
    showAinaReturnCta,
    showResolutionFeedbackUi,
  ]);

  const handleResolvedDismiss = React.useCallback(() => {
    setCloseFlowStep("none");
    pendingBreadcrumbHrefRef.current = null;
  }, []);

  const runDoubtsAiRefreshAndExit = React.useCallback(async () => {
    needsRefreshOnCloseRef.current = true;
    await refreshDoubtsAiIfNeeded();
    emitClose();
  }, [emitClose, refreshDoubtsAiIfNeeded]);
  const persistBuddyAnswerIfNeeded = React.useCallback(async () => {
    if (isMock) return;
    if (!isValidBuddySocketMarkdown(reviseFinalMarkdown) || !session.doubtId) {
      return;
    }
    const answer = reviseFinalMarkdown.trim();
    if (buddyPersistedAnswerRef.current === answer) return;
    const payload: Record<string, unknown> = {
      answer,
      doubtId: session.doubtId,
    };
    const mid = buddyMessageIdRef.current;
    if (mid != null) {
      if (typeof mid === "string" && mid.trim().length > 0) {
        payload.messageId = mid.trim();
      } else if (typeof mid === "number" && mid > 0) {
        payload.messageId = mid;
      }
    }
    try {
      const res = await setDoubtBuddyAnswerAsync({ data: payload as never });
      buddyPersistedAnswerRef.current = answer;
      const nextId = readMessageIdFromSetBuddyResponse(res);
      if (nextId != null) {
        buddyMessageIdRef.current = nextId;
      }
    } catch {
      /* best-effort; autosolve may still run */
    }
  }, [isMock, reviseFinalMarkdown, session.doubtId, setDoubtBuddyAnswerAsync]);

  React.useEffect(() => {
    if (isMock) return;
    if (revisePhase !== "complete") return;
    if (!session.doubtId) return;
    void persistBuddyAnswerIfNeeded();
  }, [
    isMock,
    persistBuddyAnswerIfNeeded,
    reviseFinalMarkdown,
    revisePhase,
    session.doubtId,
  ]);

  const handleInlineResolvedYes = React.useCallback(async () => {
    if (isMock || !session.doubtId) {
      setFinalFeedbackChoice("resolved");
      setFeedbackState("resolved");
      setFeedbackCommitted(true);
      if (showAinaReturnCta) {
        setAinaAllowBackToMain(true);
      }
      return;
    }
    if (awaitingPostReviseResolution) {
      setFeedbackState("resolving");
      try {
        await persistBuddyAnswerIfNeeded();
        await putAutosolveAsync({
          doubtId: session.doubtId,
          data: {
            doubtType: "ONLINE",
            status: "RESOLVED",
            ...(session.qbId != null && session.qbId > 0
              ? { qbId: session.qbId }
              : {}),
          },
        });
        needsRefreshOnCloseRef.current = true;
        setAwaitingPostReviseResolution(false);
        setFinalFeedbackChoice("resolved");
        setFeedbackState("resolved");
        setFeedbackCommitted(true);
        onDoubtResolved?.(session.doubtId);
        if (showAinaReturnCta) {
          setAinaAllowBackToMain(true);
        }
      } catch (e) {
        setFinalFeedbackChoice(null);
        setFeedbackState("pending");
        showSomethingWentWrong(e);
      }
      return;
    }
    setFeedbackState("resolving");
    try {
      await persistBuddyAnswerIfNeeded();
      await putAutosolveAsync({
        doubtId: session.doubtId,
        data: {
          doubtType: "ONLINE",
          status: "RESOLVED",
          ...(session.qbId != null && session.qbId > 0
            ? { qbId: session.qbId }
            : {}),
        },
      });
      needsRefreshOnCloseRef.current = true;
      setFinalFeedbackChoice("resolved");
      setFeedbackState("resolved");
      setFeedbackCommitted(true);
      onDoubtResolved?.(session.doubtId);
      if (showAinaReturnCta) {
        setAinaAllowBackToMain(true);
      }
    } catch (e) {
      setFinalFeedbackChoice(null);
      setFeedbackState("pending");
      showSomethingWentWrong(e);
    }
  }, [
    awaitingPostReviseResolution,
    doubtSession.dcNumericTenantId,
    doubtSession.gradeId,
    doubtSession.tenantId,
    doubtSession.uamId,
    isMock,
    onDoubtResolved,
    putAutosolveAsync,
    queryClient,
    session.doubtId,
    session.qbId,
    persistBuddyAnswerIfNeeded,
    showAinaReturnCta,
    showSomethingWentWrong,
  ]);

  const handleInlineResolvedNo = React.useCallback(() => {
    setAwaitingPostReviseResolution(false);
    setFinalFeedbackChoice("not-resolved");
    setFeedbackState("not-resolved");
  }, []);

  React.useEffect(() => {
    if (feedbackState !== "resolved") return;
    if (feedbackCommitted) return;
    const tId = window.setTimeout(() => {
      setFeedbackState("pending");
    }, RESOLVED_AUTO_CLOSE_MS);
    return () => window.clearTimeout(tId);
  }, [feedbackState, feedbackCommitted]);

  const handleResolvedYes = React.useCallback(async () => {
    if (isMock || !session.doubtId) {
      finishCloseChat();
      return;
    }
    try {
      await persistBuddyAnswerIfNeeded();
      await putAutosolveAsync({
        doubtId: session.doubtId,
        data: {
          doubtType: "ONLINE",
          status: "RESOLVED",
          ...(session.qbId != null && session.qbId > 0
            ? { qbId: session.qbId }
            : {}),
        },
      });
      await runDoubtsAiRefreshAndExit();
    } catch (e) {
      /** Stay on the resolved-confirmation step so the user can retry. */
      showSomethingWentWrong(e);
    }
  }, [
    finishCloseChat,
    isMock,
    persistBuddyAnswerIfNeeded,
    putAutosolveAsync,
    runDoubtsAiRefreshAndExit,
    session.doubtId,
    session.qbId,
    showSomethingWentWrong,
  ]);

  const handleResolvedNo = React.useCallback(async () => {
    if (isMock || !session.doubtId) {
      finishCloseChat();
      return;
    }
    try {
      await persistBuddyAnswerIfNeeded();
      await putAutosolveAsync({
        doubtId: session.doubtId,
        data: {
          doubtType: "ONLINE",
          status: "NOT_RESOLVED",
          ...(session.qbId != null && session.qbId > 0
            ? { qbId: session.qbId }
            : {}),
        },
      });
      await runDoubtsAiRefreshAndExit();
    } catch (e) {
      showSomethingWentWrong(e);
    }
  }, [
    finishCloseChat,
    isMock,
    persistBuddyAnswerIfNeeded,
    putAutosolveAsync,
    runDoubtsAiRefreshAndExit,
    session.doubtId,
    session.qbId,
    showSomethingWentWrong,
  ]);

  const togglePrompt = React.useCallback((id: number) => {
    setSelectedPromptIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleReviseSolution = React.useCallback(async () => {
    if (selectedPromptIds.size === 0) return;
    const buddySolutionReady =
      !isMock &&
      revisePhase === "complete" &&
      isValidBuddySocketMarkdown(reviseFinalMarkdown);

    try {
      const selected = feedbackPrompts.filter((p) =>
        selectedPromptIds.has(p.id),
      );
      const selectedTags = selected.map((p) => p.tag).filter(Boolean);
      const numericTenantId = Number(doubtSession.dcNumericTenantId);
      const feedbackService =
        buddySolutionReady || buddyPostReviseFeedbackPrompt
          ? "DOUBTBUDDY"
          : "RAG";
      await storeFeedbackAsync({
        data: {
          doubtId: session.doubtId,
          tagList: selectedTags,
          feedbackList: selectedTags,
          service: feedbackService,
          tenantId: Number.isFinite(numericTenantId)
            ? numericTenantId
            : doubtSession.tenantId,
        } as Record<string, unknown>,
      });
      setSelectedPromptIds(new Set());

      if (buddySolutionReady) {
        setFinalFeedbackChoice(null);
        setBuddyPostReviseFeedbackPrompt(true);
        return;
      }

      setFeedbackState("pending");
      setFeedbackCommitted(false);
      setFinalFeedbackChoice(null);
      setReviseFeedbackChoice("not-resolved");
      startRevise(
        session.kind === "image"
          ? {
              questionText: session.questionText,
              imageUrl: session.imageUrl,
            }
          : { questionText: session.questionText },
      );
    } catch (e) {
      showSomethingWentWrong(e);
    }
  }, [
    buddyPostReviseFeedbackPrompt,
    doubtSession.dcNumericTenantId,
    doubtSession.tenantId,
    feedbackPrompts,
    isMock,
    reviseFinalMarkdown,
    revisePhase,
    selectedPromptIds,
    session.doubtId,
    session.kind,
    session.kind === "image" ? session.imageUrl : undefined,
    session.questionText,
    showSomethingWentWrong,
    startRevise,
    storeFeedbackAsync,
  ]);

  const handleBuddyPostFeedbackYes = React.useCallback(async () => {
    if (isMock || !session.doubtId) {
      setBuddyPostReviseFeedbackPrompt(false);
      setFinalFeedbackChoice("resolved");
      setFeedbackState("resolved");
      setFeedbackCommitted(true);
      if (showAinaReturnCta) {
        setAinaAllowBackToMain(true);
      }
      return;
    }
    setFeedbackState("resolving");
    try {
      await persistBuddyAnswerIfNeeded();
      await putAutosolveAsync({
        doubtId: session.doubtId,
        data: {
          doubtType: "ONLINE",
          status: "RESOLVED",
          ...(session.qbId != null && session.qbId > 0
            ? { qbId: session.qbId }
            : {}),
        },
      });
      needsRefreshOnCloseRef.current = true;
      setBuddyPostReviseFeedbackPrompt(false);
      setAwaitingPostReviseResolution(false);
      setFinalFeedbackChoice("resolved");
      setFeedbackState("resolved");
      setFeedbackCommitted(true);
      onDoubtResolved?.(session.doubtId);
      if (showAinaReturnCta) {
        setAinaAllowBackToMain(true);
      }
    } catch (e) {
      setFinalFeedbackChoice(null);
      setFeedbackState("not-resolved");
      showSomethingWentWrong(e);
    }
  }, [
    doubtSession.dcNumericTenantId,
    doubtSession.gradeId,
    doubtSession.tenantId,
    doubtSession.uamId,
    isMock,
    onDoubtResolved,
    persistBuddyAnswerIfNeeded,
    putAutosolveAsync,
    queryClient,
    session.doubtId,
    session.qbId,
    showAinaReturnCta,
    showSomethingWentWrong,
  ]);

  const handleBuddyPostFeedbackNotYet = React.useCallback(async () => {
    if (isMock || !session.doubtId) {
      setBuddyPostReviseFeedbackPrompt(false);
      finishCloseChat();
      return;
    }
    setFeedbackState("resolving");
    try {
      await persistBuddyAnswerIfNeeded();
      await putAutosolveAsync({
        doubtId: session.doubtId,
        data: {
          doubtType: "ONLINE",
          status: "NOT_RESOLVED",
          ...(session.qbId != null && session.qbId > 0
            ? { qbId: session.qbId }
            : {}),
        },
      });
      await runDoubtsAiRefreshAndExit();
    } catch (e) {
      setFeedbackState("not-resolved");
      showSomethingWentWrong(e);
    }
  }, [
    finishCloseChat,
    isMock,
    persistBuddyAnswerIfNeeded,
    putAutosolveAsync,
    runDoubtsAiRefreshAndExit,
    session.doubtId,
    session.qbId,
    showSomethingWentWrong,
  ]);

  const handleCloseFlowOverlay = React.useCallback(() => {
    if (closingSessionBusy) return;
    if (closeFlowStep === "leave") {
      handleLeaveModalCancel();
    } else if (closeFlowStep === "resolved") {
      handleResolvedDismiss();
    }
  }, [
    closeFlowStep,
    closingSessionBusy,
    handleLeaveModalCancel,
    handleResolvedDismiss,
  ]);

  const beginDismissalAttempt = React.useCallback(() => {
    if (!showResolutionFeedbackUi && !showAinaReturnCta) {
      finishCloseChat();
      return;
    }
    if (feedbackCommitted) {
      finishCloseChat();
      return;
    }
    setCloseFlowStep("leave");
  }, [
    feedbackCommitted,
    finishCloseChat,
    showAinaReturnCta,
    showResolutionFeedbackUi,
  ]);

  const handleCloseChatHeaderPress = beginDismissalAttempt;

  React.useImperativeHandle(
    ref,
    () => ({
      requestBreadcrumbNavigation(targetHref: string) {
        pendingBreadcrumbHrefRef.current = targetHref;
        beginDismissalAttempt();
      },
    }),
    [beginDismissalAttempt],
  );

  const disclaimerModalHeader = (
    <div className="gac-header" style={{ borderBottom: "none" }}>
      <h2 className="gac-header-title-disclaimer">{t("disclaimer")}</h2>
      <button
        type="button"
        className="gac-close-btn"
        onClick={() => setDisclaimerOpen(false)}
        aria-label={t("cancel")}
      >
        <Icon
          name="close"
          width={24}
          height={24}
          color={theme.colors.primary[40]}
        />
      </button>
    </div>
  );

  const disclaimerModalBody = (
    <div className="gac-disclaimer-modal-body">
      {t("disclaimer_content")
        .split(/\n\s*\n/)
        .map((para, idx) => (
          <p className="gac-disclaimer-paragraph" key={`disclaimer-p-${idx}`}>
            {para.trim()}
          </p>
        ))}
    </div>
  );

  const reviseInProgress =
    revisePhase === "connecting" ||
    revisePhase === "processing" ||
    revisePhase === "streaming";

  const showFeedbackButtons =
    showResolutionFeedbackUi &&
    solutionReady &&
    feedbackState === "pending" &&
    !feedbackCommitted &&
    !initialAssistantLoading &&
    !reviseInProgress &&
    !buddyPostReviseFeedbackPrompt &&
    (!awaitingPostReviseResolution || reviseSolutionRevealed);

  const feedbackBusy = feedbackState === "resolving" || closingSessionBusy;
  const showFeedbackTimer =
    feedbackTimerSecondsLeft > 0 && reviseFeedbackChoice == null;

  const isFullscreen = layout === "fullscreen";

  /** Top strip: same exit path as header (leave → autosolve) until inline resolved is done. */
  const showAinaReturnTop = showAinaReturnCta && !ainaAllowBackToMain;
  /** Floating pill: after inline resolved + feedback path — direct return to AINA. */
  const showAinaReturnFloating = showAinaReturnCta && ainaAllowBackToMain;

  const handleAinaReturnPress = React.useCallback(() => {
    if (ainaAllowBackToMain) {
      emitClose({ navigateToAina: true });
      return;
    }
    setCloseFlowStep("leave");
  }, [ainaAllowBackToMain, emitClose]);

  const ainaReturnTopStrip = showAinaReturnTop ? (
    <div
      className={
        ainaEmbedded
          ? "gac-aina-return gac-aina-return--inline"
          : "gac-aina-return gac-aina-return--top"
      }
    >
      <button
        type="button"
        className={
          ainaEmbedded
            ? "gac-aina-return-btn gac-aina-return-btn--embedded"
            : "gac-aina-return-btn"
        }
        onClick={handleAinaReturnPress}
        aria-label={t("go_back_to_main_conversation_a11y")}
      >
        <span className="gac-aina-return-label">
          {t("go_back_to_main_conversation")}
        </span>
        <span className="gac-aina-return-avatar" aria-hidden>
          <Icon name="ainaWidgetIcon" width={40} height={40} />
        </span>
      </button>
    </div>
  ) : null;

  const embeddedSplitChrome = ainaEmbedded && showAinaReturnTop;

  React.useLayoutEffect(() => {
    syncChatScrollElevation();
  }, [
    syncChatScrollElevation,
    initialAssistantLoading,
    initialAssistant,
    feedbackState,
    reviseSolutionRevealed,
    revisePhase,
    reviseFinalMarkdown,
    embeddedSplitChrome,
    isFullscreen,
    ainaEmbedded,
  ]);

  const userQuestionRowJsx = (
    <div className="gac-user-row gac-user-row--image">
      <div className="gac-image-col">
        {!showAinaSolutionAvatar ? (
          <div className="gac-avatar gac-avatar--above" aria-hidden>
            <UserChatAvatar />
          </div>
        ) : null}
        {session.kind === "image" ? (
          <div className="gac-user-bubble--image-wrap">
            <button
              type="button"
              className="gac-user-bubble-img-btn"
              onClick={() => setImagePreviewOpen(true)}
              aria-label={t("preview_doubt_image_a11y", {
                defaultValue: "Preview doubt image",
              })}
            >
              <img
                src={session.imageUrl}
                alt=""
                className="gac-user-bubble-img"
              />
            </button>
            {session.questionText ? (
              <MarkdownInline className="gac-user-bubble-img-caption">
                {session.questionText}
              </MarkdownInline>
            ) : null}
          </div>
        ) : (
          <div className="gac-user-bubble">
            <MarkdownInline className="gac-user-bubble-md">
              {session.questionText}
            </MarkdownInline>
          </div>
        )}
      </div>
    </div>
  );

  const scrollFirstQuestionBlock = embeddedSplitChrome
    ? null
    : userQuestionRowJsx;

  const disclaimerBaseClassName = embeddedSplitChrome
    ? "gac-disclaimer-block gac-disclaimer-block--with-close gac-disclaimer-block--embedded-chrome"
    : ainaEmbedded
      ? "gac-disclaimer-block gac-disclaimer-block--sticky gac-disclaimer-block--with-close"
      : isFullscreen
        ? "gac-disclaimer-block gac-disclaimer-block--sticky"
        : "gac-disclaimer-block";

  const disclaimerElevatedOnScroll =
    chatScrollElevated &&
    (embeddedSplitChrome ||
      (!embeddedSplitChrome && (ainaEmbedded || isFullscreen)));

  const disclaimerClassName = [
    disclaimerBaseClassName,
    disclaimerElevatedOnScroll ? "gac-disclaimer-block--elevated" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const disclaimerConsentText = t("chat_disclaimer_consent");
  const disclaimerConsentLines = React.useMemo(() => {
    const marker = "provided,";
    const markerIdx = disclaimerConsentText.indexOf(marker);
    if (markerIdx < 0) return [disclaimerConsentText];
    const splitAt = markerIdx + marker.length;
    return [
      disclaimerConsentText.slice(0, splitAt),
      disclaimerConsentText.slice(splitAt).trimStart(),
    ];
  }, [disclaimerConsentText]);

  const disclaimerEl = (
    <div className={disclaimerClassName}>
      <p className="gac-disclaimer-text">
        {disclaimerConsentLines[0]}
        {disclaimerConsentLines.length > 1 ? (
          <>
            <br />
            {disclaimerConsentLines[1]}
          </>
        ) : null}
      </p>
      <button
        type="button"
        className="gac-disclaimer-link"
        onClick={() => setDisclaimerOpen(true)}
      >
        {t("disclaimer")}
      </button>
      {ainaEmbedded ? (
        <button
          type="button"
          className="gac-disclaimer-close-btn"
          onClick={handleCloseChatHeaderPress}
          aria-label={t("close_chat_a11y")}
        >
          <Icon
            name="chatCloseIcon"
            width={24}
            height={24}
            color={theme.colors.primary[40]}
          />
        </button>
      ) : null}
    </div>
  );

  const showGenerateErrorEl = showGenerateError ? (
    <div className="gac-error-banner" role="alert">
      <p className="gac-error-banner-text">{t("solution_load_failed")}</p>
      <button
        type="button"
        className="gac-error-retry"
        onClick={handleRetryGenerate}
      >
        {t("solution_retry")}
      </button>
    </div>
  ) : null;

  const scrollAreaClassName = showAinaReturnFloating
    ? "gac-scroll gac-scroll--aina gac-scroll--has-aina-fab"
    : showAinaSolutionAvatar
      ? "gac-scroll gac-scroll--aina"
      : "gac-scroll";

  const chatThreadTail = (
    <>
      {showAinaSolutionAvatar &&
      (showResolutionFeedbackUi || showAssistantRagPanels) ? (
        <div className="gac-assistant-stack">
          <span className="gac-aina-return-avatar" aria-hidden>
            <Icon name="ainaWidgetIcon" width={40} height={40} />
          </span>
          <div className="gac-assistant-stack-body">
            {showResolutionFeedbackUi ? (
              <div className="gac-assistant-intro gac-assistant-intro--in-aina-stack">
                <p className="gac-assistant-intro-text">{intro}</p>
              </div>
            ) : null}
            {showAssistantRagPanels ? (
              <AssistantRagPanels
                keyBase="initial"
                rag={initialAssistant ?? emptyRag}
                isLoading={initialAssistantLoading}
                theme={theme}
                t={t}
                centerLoadingInPane
              />
            ) : null}
          </div>
        </div>
      ) : (
        <>
          {showResolutionFeedbackUi ? (
            <div className="gac-assistant-intro">
              <p className="gac-assistant-intro-text">{intro}</p>
            </div>
          ) : null}
          {showAssistantRagPanels ? (
            <AssistantRagPanels
              keyBase="initial"
              rag={initialAssistant ?? emptyRag}
              isLoading={initialAssistantLoading}
              theme={theme}
              t={t}
            />
          ) : null}
        </>
      )}

      {revisePhase !== "idle" && reviseFeedbackChoice ? (
        <>
          <div className="gac-user-row gac-user-row--image">
            <div className="gac-image-col">
              {!showAinaSolutionAvatar ? (
                <div className="gac-avatar gac-avatar--above" aria-hidden>
                  <UserChatAvatar />
                </div>
              ) : null}
              <div className="gac-user-bubble">
                {reviseFeedbackChoice === "resolved"
                  ? t("my_doubt_is_resolved")
                  : t("my_doubt_is_not_resolved")}
              </div>
            </div>
          </div>

          {!showAinaSolutionAvatar &&
          reviseFeedbackChoice === "not-resolved" ? (
            <div className="gac-assistant-feedback-row">
              <div className="gac-assistant-intro">{feedbackThanksMessage}</div>
            </div>
          ) : null}
        </>
      ) : null}

      {revisePhase !== "idle" && revisePhase !== "error" ? (
        showAinaSolutionAvatar ? (
          <div className="gac-assistant-stack">
            <span className="gac-aina-return-avatar" aria-hidden>
              <Icon name="ainaWidgetIcon" width={40} height={40} />
            </span>
            <div className="gac-assistant-stack-body">
              {reviseFeedbackChoice === "not-resolved" ? (
                <div className="gac-assistant-intro gac-assistant-intro--in-aina-stack">
                  {feedbackThanksMessage}
                </div>
              ) : null}
              <MarkdownSolution
                phase={revisePhase}
                finalMarkdown={reviseFinalMarkdown}
                error={reviseError}
                loadingLabel={t("revise_generating")}
                loadingInAinaStack={showAinaSolutionAvatar}
                forceFullSolution={
                  initialGenerateBuddyFallback && reviseFeedbackChoice == null
                }
                onSolutionRevealed={() => {
                  setReviseSolutionRevealed(true);
                  setFeedbackTimerSecondsLeft(
                    reviseFeedbackChoice ? 0 : FEEDBACK_TIMER_SECONDS,
                  );
                  window.requestAnimationFrame(() => {
                    scrollToBottom();
                  });
                }}
                onDoneResolved={() => {
                  void handleInlineResolvedYes();
                }}
              />
            </div>
          </div>
        ) : (
          <MarkdownSolution
            phase={revisePhase}
            finalMarkdown={reviseFinalMarkdown}
            error={reviseError}
            loadingLabel={t("revise_generating")}
            forceFullSolution={
              initialGenerateBuddyFallback && reviseFeedbackChoice == null
            }
            onSolutionRevealed={() => {
              setReviseSolutionRevealed(true);
              setFeedbackTimerSecondsLeft(
                reviseFeedbackChoice ? 0 : FEEDBACK_TIMER_SECONDS,
              );
              window.requestAnimationFrame(() => {
                scrollToBottom();
              });
            }}
            onDoneResolved={() => {
              void handleInlineResolvedYes();
            }}
          />
        )
      ) : null}

      {showFeedbackButtons ? (
        showAinaSolutionAvatar ? (
          <div className="gac-feedback-aina-wrap">
            <div className="gac-feedback-row gac-feedback-row--aina">
              <button
                type="button"
                className="gac-feedback-btn"
                onClick={() => void handleInlineResolvedYes()}
                disabled={feedbackBusy}
              >
                {t("my_doubt_is_resolved")}
              </button>
              <button
                type="button"
                className={
                  showFeedbackTimer
                    ? "gac-feedback-btn gac-feedback-btn--not-resolved"
                    : "gac-feedback-btn"
                }
                onClick={() => void handleInlineResolvedNo()}
                disabled={showFeedbackTimer || feedbackBusy}
              >
                {showFeedbackTimer ? (
                  <>
                    <span
                      className="gac-feedback-btn-fill"
                      style={{
                        transform: `scaleX(${feedbackTimerSecondsLeft / FEEDBACK_TIMER_SECONDS})`,
                      }}
                    />
                    <span className="gac-feedback-btn-label">
                      {t("my_doubt_is_not_resolved")}
                    </span>
                  </>
                ) : (
                  t("my_doubt_is_not_resolved")
                )}
              </button>
            </div>
            {showFeedbackTimer ? (
              <p className="gac-feedback-timer-text gac-feedback-timer-text--aina">
                {t("review_solution_timer")}
                <span className="gac-timer-value">
                  {`${String(Math.floor(feedbackTimerSecondsLeft / 60)).padStart(2, "0")}:${String(feedbackTimerSecondsLeft % 60).padStart(2, "0")}`}
                </span>
              </p>
            ) : null}
          </div>
        ) : (
          <div className="gac-feedback-inline-wrap">
            <div className="gac-feedback-row">
              <button
                type="button"
                className="gac-feedback-btn"
                onClick={() => void handleInlineResolvedYes()}
                disabled={feedbackBusy}
              >
                {t("my_doubt_is_resolved")}
              </button>
              <button
                type="button"
                className={
                  showFeedbackTimer
                    ? "gac-feedback-btn gac-feedback-btn--not-resolved"
                    : "gac-feedback-btn"
                }
                onClick={() => void handleInlineResolvedNo()}
                disabled={showFeedbackTimer || feedbackBusy}
              >
                {showFeedbackTimer ? (
                  <>
                    <span
                      className="gac-feedback-btn-fill"
                      style={{
                        transform: `scaleX(${feedbackTimerSecondsLeft / FEEDBACK_TIMER_SECONDS})`,
                      }}
                    />
                    <span className="gac-feedback-btn-label">
                      {t("my_doubt_is_not_resolved")}
                    </span>
                  </>
                ) : (
                  t("my_doubt_is_not_resolved")
                )}
              </button>
            </div>
            {showFeedbackTimer ? (
              <p className="gac-feedback-timer-text">
                {t("review_solution_timer")}
                <span className="gac-timer-value">
                  {`${String(Math.floor(feedbackTimerSecondsLeft / 60)).padStart(2, "0")}:${String(feedbackTimerSecondsLeft % 60).padStart(2, "0")}`}
                </span>
              </p>
            ) : null}
          </div>
        )
      ) : null}

      {showResolutionFeedbackUi &&
      finalFeedbackChoice === "resolved" &&
      feedbackState === "resolved" ? (
        <div className="gac-user-row gac-user-row--image">
          <div className="gac-image-col">
            {!showAinaSolutionAvatar ? (
              <div className="gac-avatar gac-avatar--above" aria-hidden>
                <UserChatAvatar />
              </div>
            ) : null}
            <div className="gac-user-bubble">{t("my_doubt_is_resolved")}</div>
          </div>
        </div>
      ) : null}

      {showResolutionFeedbackUi && feedbackState === "resolved" ? (
        <div className="gac-success-view">
          <Icon name="successIllustration" width={200} height={200} />
          <p className="gac-success-text">{t("we_are_glad")}</p>
        </div>
      ) : null}

      {showResolutionFeedbackUi && buddyPostReviseFeedbackPrompt ? (
        <div className="gac-is-doubt-solved-card">
          <span className="gac-is-doubt-solved-emoji" aria-hidden>
            😊
          </span>
          <p className="gac-is-doubt-solved-title">{t("is_doubt_solved")}</p>
          <div className="gac-is-doubt-solved-actions">
            <button
              type="button"
              className="gac-is-doubt-solved-btn gac-is-doubt-solved-btn--outline"
              disabled={feedbackBusy}
              onClick={() => void handleBuddyPostFeedbackNotYet()}
            >
              {t("not_yet")}
            </button>
            <button
              type="button"
              className="gac-is-doubt-solved-btn gac-is-doubt-solved-btn--primary"
              disabled={feedbackBusy}
              onClick={() => void handleBuddyPostFeedbackYes()}
            >
              {t("yes")}
            </button>
          </div>
        </div>
      ) : null}

      {showResolutionFeedbackUi &&
      !buddyPostReviseFeedbackPrompt &&
      (feedbackState === "not-resolved" || feedbackState === "resolving") &&
      solutionReady ? (
        <div className="gac-user-row gac-user-row--image">
          <div className="gac-image-col">
            {!showAinaSolutionAvatar ? (
              <div className="gac-avatar gac-avatar--above" aria-hidden>
                <UserChatAvatar />
              </div>
            ) : null}
            <div className="gac-user-bubble">
              {t("my_doubt_is_not_resolved")}
            </div>
          </div>
        </div>
      ) : null}

      {showResolutionFeedbackUi &&
      finalFeedbackChoice === "not-resolved" &&
      (feedbackState === "not-resolved" || feedbackState === "resolving") &&
      solutionReady ? (
        showAinaSolutionAvatar ? (
          <div className="gac-assistant-stack">
            <span className="gac-aina-return-avatar" aria-hidden>
              <Icon name="ainaWidgetIcon" width={40} height={40} />
            </span>
            <div className="gac-assistant-stack-body">
              <div className="gac-assistant-intro gac-assistant-intro--in-aina-stack">
                {feedbackThanksMessage}
              </div>
            </div>
          </div>
        ) : (
          <div className="gac-assistant-intro">{feedbackThanksMessage}</div>
        )
      ) : null}

      {showResolutionFeedbackUi &&
      feedbackState === "not-resolved" &&
      !buddyPostReviseFeedbackPrompt ? (
        <div className="gac-not-resolved-card">
          <Icon name="emojiSad" width={64} height={64} color="inherit" />
          <p className="gac-not-resolved-title">
            {t("sorry_unresolved", {
              name: resolvedFirstName,
            })}
          </p>
          <p className="gac-not-resolved-subtitle">
            {t("please_provide_concern")}
          </p>
          <div className="gac-not-resolved-pills">
            {feedbackPrompts.map((prompt) => (
              <button
                key={prompt.id}
                type="button"
                className={
                  selectedPromptIds.has(prompt.id)
                    ? "gac-not-resolved-pill gac-not-resolved-pill--selected"
                    : "gac-not-resolved-pill"
                }
                onClick={() => togglePrompt(prompt.id)}
              >
                {getNotResolvedPillLabel(prompt)}
              </button>
            ))}
          </div>
          <div className="gac-not-resolved-actions">
            <button
              type="button"
              className="gac-revise-btn"
              disabled={selectedPromptIds.size === 0 || isStoreFeedbackPending}
              onClick={() => void handleReviseSolution()}
            >
              {t("revise_solution")}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <div
      className={
        isFullscreen
          ? "gac-root gac-root--fullscreen"
          : embeddedSplitChrome
            ? "gac-root gac-root--embedded-split"
            : "gac-root"
      }
    >
      {ainaEmbedded ? null : (
        <header
          className={
            isFullscreen ? "gac-header gac-header--fullscreen" : "gac-header"
          }
        >
          {isFullscreen ? (
            <>
              <button
                type="button"
                className="gac-back-circle-btn"
                onClick={handleCloseChatHeaderPress}
                aria-label={t("close_chat_a11y")}
              >
                <Icon name="circleBackBtn" width={48} height={48} />
              </button>
              <h2 className="gac-header-title gac-header-title--center">
                {t("get_answers_title")}
              </h2>
              <span className="gac-header-spacer" aria-hidden />
            </>
          ) : (
            <>
              <div className="gac-header-title-web">
                {t("get_answers_title")}
              </div>
              <button
                type="button"
                className="gac-close-btn"
                onClick={handleCloseChatHeaderPress}
                aria-label={t("close_chat_a11y")}
              >
                <Icon
                  name="chatCloseIcon"
                  width={40}
                  height={40}
                  color={theme.colors.primary[40]}
                />
              </button>
            </>
          )}
        </header>
      )}

      {!ainaEmbedded ? ainaReturnTopStrip : null}

      {embeddedSplitChrome ? (
        <div className="gac-embedded-scroll-shell">
          <div className="gac-embedded-chrome">
            {disclaimerEl}
            {ainaReturnTopStrip}
          </div>
          <div
            ref={scrollRef}
            onScroll={handleChatScroll}
            className={`${scrollAreaClassName} gac-scroll--embedded-split-messages`}
          >
            {showGenerateErrorEl}
            {userQuestionRowJsx}
            {chatThreadTail}
          </div>
        </div>
      ) : (
        <div
          className={scrollAreaClassName}
          ref={scrollRef}
          onScroll={handleChatScroll}
        >
          {disclaimerEl}
          {showGenerateErrorEl}
          {scrollFirstQuestionBlock}
          {chatThreadTail}
        </div>
      )}

      <Modal
        isModalOpen={disclaimerOpen}
        onClose={() => setDisclaimerOpen(false)}
        showFooter={false}
        showHeader
        customHeader={disclaimerModalHeader}
        body={disclaimerModalBody}
        width="min(64rem, 94vw)"
        height="auto"
        alignItems="center"
      />

      <Modal
        isModalOpen={closeFlowStep !== "none"}
        onClose={handleCloseFlowOverlay}
        showFooter={false}
        showHeader={false}
        justifyContent={isFullscreen ? "flex-end" : "center"}
        alignItems={isFullscreen ? "flex-start" : "center"}
        width={isFullscreen ? "100%" : 640}
        height={isFullscreen ? "auto" : 374}
        modalClassName={isFullscreen ? "gac-close-flow-modal" : undefined}
        body={
          <div
            className={[
              "gac-confirm-card",
              closeFlowStep === "leave" ? "gac-confirm-card--leave" : "",
              isFullscreen
                ? "gac-confirm-card--fullscreen"
                : "gac-confirm-card--web",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {(closeFlowStep === "leave" || closeFlowStep === "resolved") && (
              <button
                type="button"
                className="gac-confirm-close"
                onClick={handleCloseFlowOverlay}
                disabled={closingSessionBusy}
                aria-label={t("cancel")}
              >
                <Icon
                  name="close"
                  width={24}
                  height={24}
                  color={theme.colors.primary[40]}
                />
              </button>
            )}
            {closeFlowStep === "leave" ? (
              <div className="gac-confirm-content gac-confirm-content--leave">
                <div className="gac-confirm-title-wrap gac-confirm-title-wrap--leave">
                  <p className="gac-confirm-title">{t("are_you_sure_leave")}</p>
                </div>
                <div className="gac-confirm-actions">
                  <button
                    type="button"
                    className="gac-confirm-link"
                    onClick={handleLeaveModalCancel}
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="button"
                    className="gac-confirm-primary"
                    onClick={handleLeaveModalConfirm}
                  >
                    {t("leave")}
                  </button>
                </div>
              </div>
            ) : closeFlowStep === "resolved" ? (
              <div className="gac-confirm-content gac-confirm-content--resolved">
                <div className="gac-confirm-title-wrap gac-confirm-title-wrap--resolved">
                  <p className="gac-confirm-title">
                    {t("was_your_doubt_resolved")}
                  </p>
                </div>
                <div className="gac-confirm-actions">
                  <button
                    type="button"
                    className="gac-confirm-link"
                    onClick={() => void handleResolvedNo()}
                    disabled={closingSessionBusy}
                  >
                    {t("no")}
                  </button>
                  <button
                    type="button"
                    className="gac-confirm-primary"
                    onClick={() => void handleResolvedYes()}
                    disabled={closingSessionBusy}
                  >
                    {t("yes")}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        }
        styles={{
          modal: {
            borderRadius: "var(--radii-2xl)",
            padding: 0,
            overflow: "visible",
            ...(isFullscreen ? { width: "100%", maxWidth: "100%" } : undefined),
          },
          body: {
            padding: 0,
            overflow: "visible",
          },
        }}
      />

      {session.kind === "image" ? (
        <Modal
          isModalOpen={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          showHeader={false}
          showFooter={false}
          width="min(92vw, 960px)"
          height="auto"
          alignItems="center"
          body={
            <div className="gac-image-preview">
              <button
                type="button"
                className="gac-image-preview-close"
                onClick={() => setImagePreviewOpen(false)}
                aria-label={t("close")}
              >
                <Icon
                  name="close"
                  width={20}
                  height={20}
                  color={theme.colors.neutral[20]}
                />
              </button>
              <div className="gac-image-preview-img-wrap">
                <img
                  src={session.imageUrl}
                  alt=""
                  className="gac-image-preview-img"
                />
              </div>
              {session.questionText ? (
                <MarkdownInline className="gac-image-preview-caption">
                  {session.questionText}
                </MarkdownInline>
              ) : null}
            </div>
          }
          styles={{
            modal: {
              borderRadius: "var(--radii-2xl)",
              padding: 0,
              overflow: "visible",
            },
            body: {
              padding: 0,
              overflow: "visible",
            },
          }}
        />
      ) : null}

      {showAinaReturnFloating ? (
        <div className="gac-aina-return gac-aina-return--floating">
          <button
            type="button"
            className="gac-aina-return-btn gac-aina-return-btn--floating"
            onClick={handleAinaReturnPress}
            aria-label={t("go_back_to_main_conversation_a11y")}
          >
            <span className="gac-aina-return-label">
              {t("go_back_to_main_conversation")}
            </span>
            <span className="gac-aina-return-avatar" aria-hidden>
              <Icon name="ainaWidgetIcon" width={40} height={40} />
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
});

GetAnswersChat.displayName = "GetAnswersChat";

export default GetAnswersChat;
